// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { industrySectors } from "@/plugins/LocalForagePlugin";

// TMI - content type and query definitions
import { IndustrySector } from "@tmi/types/entities";
import { refreshIndustrySectorsFromServer } from "@/services/data/sync/industry-sector-sync-coordinator";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allIndustrySectors.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const defaultIndustrySectors = [] as unknown as IndustrySector[];
const allIndustrySectors = ref< IndustrySector[] >( defaultIndustrySectors );

export const useIndustrySectorsStore = defineStore( 'industry-sectors', () => {
    function reset(): void {
        allIndustrySectors.value.splice( 0 );
        loading.value = false;
        refreshing.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ IndustrySectors ].initialize() exiting - already processing...' );
            return;
        }

        logger.debug( `[ IndustrySectors ] Checking the local database for any industry sectors...` );

        loading.value = true;

        const t0: number = performance.now();

        if ( typeof industrySectors === 'undefined' ) {
            // Wait for the driver to settle
            await LocalForagePlugin.initialize();
        }

        await industrySectors.ready();
        const numberIndustrySectors = await industrySectors.length();

        const uniqueIndustrySectors: Map< string, IndustrySector > = new Map<string, IndustrySector>();
        if ( numberIndustrySectors > 0 ) {
            // Clear the contents of the collections
            allIndustrySectors.value.splice( 0 );

            // Iterate over all elements within IDB
            industrySectors.iterate( ( industrySector: IndustrySector ) => {
                uniqueIndustrySectors.set( industrySector.id, cloneDeep( industrySector ) );
                return;
            } ).then( () => {
                allIndustrySectors.value = [ ...( Array.from( uniqueIndustrySectors.values() ) ) ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allIndustrySectors.value.length } industry sectors in ${ t1 - t0 } ms` );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.message ) );
                reset();
            } ).finally( () => {
                // Clear the "loading" flag
                loading.value = false;
                refreshing.value = false;
            } );
        } else {
            if ( refreshing.value ) {
                return;
            }

            refreshing.value = true;

            // Timing - testing to see how expensive each method is
            const t0: number = performance.now();

            refreshIndustrySectorsFromServer()
                .then( ( receivedIndustrySectors: IndustrySector[] ) => allIndustrySectors.value = [ ...receivedIndustrySectors ] )
                .catch( ( err: any ) => {
                    logger.error( JSON.stringify( err.message ) );
                    reset();
                } )
                .finally( () => {
                    // Clear the "loading" flag
                    loading.value = false;
                    refreshing.value = false;

                    const t1: number = performance.now();
                    logger.info( `Fetching & mapping ${ allIndustrySectors.value.length } industry sector items took ${ t1 - t0 } ms` );
                } );
        }

        return
    }

    function industrySectorById( id: string ): IndustrySector | undefined {
        return allIndustrySectors.value.find( ( sector: IndustrySector ): boolean => sector.id === id );
    }

    return {
        // ACTIONS
        industrySectorById,
        initialize,
        reset,

        // GETTERS
        allIndustrySectors
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useIndustrySectorsStore, import.meta.hot ) );
}
